// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import {
  AddRounded,
  InfoOutlined,
  SettingsRounded,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Location } from '@/location/api/types';
import { updateLocation } from '@/location/api/update-location';
import { CreateZoneDialog } from '@/location/components/create-zone-dialog';
import { EditZoneDialog } from '@/location/components/edit-zone-dialog';
import { MergeZoneDialog } from '@/location/components/merge-zone-dialog';
import { ZonesTable } from '@/location/components/zones-table';
import { useZones } from '@/location/hooks/use-zones';
import { SparseLocation } from '@/sparse/api/types';
import {
  Badge,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Suspense, useRef, useState } from 'react';

const SHOW_ARCHIVED_ZONES_TOOLTIP =
  'Show archived zone records. Archived zones do not appear by default.';

const ZONES_INFO_TOOLTIP =
  'Zones are optional individual areas within a location that can be managed separately. Each location has a "Default" zone if no zones are configured. You do not need to configure zones to use a location. Example uses of zones: Receiving, Provisioning, Staging, Maintenance, Hold, Outbound';

interface ZonesTabContentProps {
  location: Location;
  locationKey: string;
}

export function ZonesTabContent(props: ZonesTabContentProps) {
  const queryClient = useQueryClient();
  const createZoneDialog = useDisclosure();
  const editZoneDialog = useDisclosure();
  const mergeZoneDialog = useDisclosure();
  const settingsMenu = useDisclosure();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [selectedZone, setSelectedZone] = useState<SparseLocation | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  const archiveZoneMutation = useMutation({
    mutationFn: (params: { key: string; isArchived: boolean }) =>
      updateLocation(params.key, 'ZONE', { isArchived: params.isArchived }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location'] });
    },
  });

  const handleEditZone = (zone: SparseLocation) => {
    setSelectedZone(zone);
    editZoneDialog.open();
  };

  const handleMergeZone = (zone: SparseLocation) => {
    setSelectedZone(zone);
    mergeZoneDialog.open();
  };

  const handleArchiveZone = (zone: SparseLocation, isArchived: boolean) => {
    archiveZoneMutation.mutate({ key: zone.key, isArchived });
  };

  const handleChangeArchived = () => {
    setIncludeArchived((prev) => !prev);
  };

  return (
    <>
      <CreateZoneDialog
        locationId={props.location.id}
        locationKey={props.locationKey}
        isOpen={createZoneDialog.isOpen}
        onClose={createZoneDialog.close}
      />
      {selectedZone && (
        <EditZoneDialog
          zone={selectedZone}
          locationKey={props.locationKey}
          isOpen={editZoneDialog.isOpen}
          onClose={editZoneDialog.close}
        />
      )}
      {selectedZone && mergeZoneDialog.isOpen && (
        <MergeZoneDialog
          zoneKey={selectedZone.key}
          isOpen={mergeZoneDialog.isOpen}
          onClose={mergeZoneDialog.close}
        />
      )}
      <HardfinTabCardWrapper
        title={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <span>Zones</span>
            <Tooltip arrow placement="bottom" title={ZONES_INFO_TOOLTIP}>
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              color="secondary"
              variant="contained"
              startIcon={<AddRounded />}
              onClick={createZoneDialog.open}
            >
              Add zone
            </Button>
            <IconButton
              ref={menuButtonRef}
              size="small"
              onClick={settingsMenu.open}
            >
              <Badge variant="dot" color="error" invisible={!includeArchived}>
                <SettingsRounded fontSize="small" />
              </Badge>
            </IconButton>
            {settingsMenu.isOpen && (
              <Popover
                anchorEl={menuButtonRef.current}
                open
                onClose={settingsMenu.close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Stack paddingY={0.5}>
                  <Tooltip
                    arrow
                    placement="left"
                    title={SHOW_ARCHIVED_ZONES_TOOLTIP}
                  >
                    <FormControlLabel
                      sx={{ paddingLeft: 2, paddingY: 0.5 }}
                      label={
                        <Typography variant="body2">
                          Include archived zones
                        </Typography>
                      }
                      control={
                        <Checkbox
                          size="small"
                          checked={includeArchived}
                          onChange={handleChangeArchived}
                        />
                      }
                    />
                  </Tooltip>
                </Stack>
              </Popover>
            )}
          </Stack>
        }
      >
        <Stack paddingTop={2}>
          <Suspense>
            <ZonesTableContent
              location={props.location}
              locationKey={props.locationKey}
              includeArchived={includeArchived}
              onEditZone={handleEditZone}
              onMergeZone={handleMergeZone}
              onArchiveZone={handleArchiveZone}
            />
          </Suspense>
        </Stack>
      </HardfinTabCardWrapper>
    </>
  );
}

interface ZonesTableContentProps {
  location: Location;
  locationKey: string;
  includeArchived: boolean;
  onEditZone: (zone: SparseLocation) => void;
  onMergeZone: (zone: SparseLocation) => void;
  onArchiveZone: (zone: SparseLocation, isArchived: boolean) => void;
}

function ZonesTableContent(props: ZonesTableContentProps) {
  const { zones } = useZones(props.locationKey, {
    includeArchived: props.includeArchived,
  });

  // Prepend the parent site as the default zone entry
  const defaultZone = { ...props.location, name: 'Default zone' };
  const allZones = [defaultZone, ...zones];

  return (
    <ZonesTable
      zones={allZones}
      onEditZone={props.onEditZone}
      onMergeZone={props.onMergeZone}
      onArchiveZone={props.onArchiveZone}
    />
  );
}
